import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useIsThreeDotsMenuVisible } from './useIsThreeDotsMenuVisible';

import { ContainerWrapper } from 'features/call/call-details/styles';
import { useAppSelector } from 'features/app/hooks';
import {
  useCallDeafParticipantHandlers,
  useCallHearingParticipantHandlers,
} from 'features/call/call-accepted/hooks';
import { callSelector } from 'features/call/call-base/store/selectors';
import {
  deafIsLoadingSelector,
  deafSelector,
  isDeafReconnectSelector,
  selectIsDeafUserFromSorenson,
} from 'features/call/call-deaf/store';
import { isCalleeReconnectSelector } from 'features/call/call-details/store';
import {
  CallDetailsButtonCalleeConnect,
  CallDetailsButtonCancel,
  CallDetailsButtonDisconnect,
  CallDetailsButtonHearingDisconnect,
  CallDetailsButtonReconnect,
  CallDetailsParticipantIcon,
  CallDetailsSkipToSignMailButton,
  CallDetailsDeafConnectedButtons,
} from 'features/call/call-details/components';
import type { CallDetailsParticipantInfo } from 'features/call/call-details/interfaces';
import {
  hearingWSConnectionSelector,
  isAnyHearingConnectingSelector,
  isPrimaryHearingReconnectSelector,
  primaryHearingSelector,
  selectIsLoadingPrimaryHearing,
  selectIsLoadingVco2LineHearing,
} from 'features/call/call-hearing/store';
import { DeafPhoneNumberHidden } from 'features/deaf-phone-number-hidden/components';
import { useSignMailHandlers } from 'features/signmail/hooks';
import { CallDetailsButtonNewCall } from 'features/spawn-call/components';
import { useTeamingUserConnectionStatusHandlers } from 'features/teaming/teaming-base/hooks';
import {
  isAcceptingUserSelector,
  isTeamingInProgressSelector,
} from 'features/teaming/teaming-base/store';
import { CallDetailsParticipantVco2LineContainer } from 'features/vco-2-line/components';
import { voiceSessionStatusSelector } from 'features/voice-session/store';

export const useCallDetailsCallee = () => {
  const { handleRegularHearingCall, handleHearingDisconnect } =
    useCallHearingParticipantHandlers();
  const { handleDeafConnect, handleDeafCancel } =
    useCallDeafParticipantHandlers();
  const { handleSkipToSignMail, handleEndSignMailSession } =
    useSignMailHandlers();

  const { isDeafToHearing } = useAppSelector(callSelector);
  const deaf = useAppSelector(deafSelector);
  const isCalleeReconnect = useAppSelector(isCalleeReconnectSelector);
  const primaryHearing = useAppSelector(primaryHearingSelector);
  const isLoadingDeaf = useAppSelector(deafIsLoadingSelector);
  const voiceSessionStatus = useAppSelector(voiceSessionStatusSelector);
  const isWSConnected = useAppSelector(hearingWSConnectionSelector);
  const isTeamingInProgress = useAppSelector(isTeamingInProgressSelector);
  const isAcceptingUser = useAppSelector(isAcceptingUserSelector);
  const isLoadingPrimaryHearing = useAppSelector(selectIsLoadingPrimaryHearing);
  const isAnyHearingConnecting = useAppSelector(isAnyHearingConnectingSelector);
  const isLoadingVco2LineHearing = useAppSelector(
    selectIsLoadingVco2LineHearing
  );

  const isPrimaryHearingReconnect = useAppSelector(
    isPrimaryHearingReconnectSelector
  );

  const isDeafReconnect = useAppSelector(isDeafReconnectSelector);

  const { t } = useTranslation('translation', { keyPrefix: 'callDetails' });

  const { isUserStatusReceived } = useTeamingUserConnectionStatusHandlers();
  const isDeafUserFromSorenson = useAppSelector(selectIsDeafUserFromSorenson);
  const isThreeDotsMenuButtonPresent = useIsThreeDotsMenuVisible();

  const isTeamingConnectionInProgress =
    isTeamingInProgress && !isUserStatusReceived;

  const isHearingReconnect = useMemo(
    () => primaryHearing.status === 'disconnected' && isCalleeReconnect,
    [primaryHearing.status, isCalleeReconnect]
  );

  let calleeTitle: string;
  if (isDeafToHearing) {
    calleeTitle = isPrimaryHearingReconnect ? t('lastCaller') : t('callTo');
  } else {
    calleeTitle = isDeafReconnect ? t('lastCaller') : t('callTo');
  }

  const deafToHearingCalleeButton = useMemo(() => {
    if (isAcceptingUser) {
      return null;
    }

    if (isHearingReconnect) {
      return (
        <>
          {isPrimaryHearingReconnect ? (
            <CallDetailsButtonReconnect
              disabled={isAnyHearingConnecting || isLoadingVco2LineHearing}
              onClick={() =>
                handleRegularHearingCall(primaryHearing.phoneNumber)
              }
            />
          ) : (
            <CallDetailsButtonCalleeConnect
              isLoading={false}
              onClickHandler={() =>
                handleRegularHearingCall(primaryHearing.phoneNumber)
              }
            />
          )}
          <ContainerWrapper>
            <CallDetailsButtonNewCall />
          </ContainerWrapper>
        </>
      );
    }

    if (primaryHearing.status === 'disconnected') {
      return (
        <DeafPhoneNumberHidden>
          <ContainerWrapper>
            <CallDetailsButtonNewCall
              isLoading={isTeamingConnectionInProgress}
            />
          </ContainerWrapper>
        </DeafPhoneNumberHidden>
      );
    }

    if (primaryHearing.status === 'connecting') {
      return (
        <CallDetailsButtonCancel
          loading={
            isLoadingPrimaryHearing || !primaryHearing.id || !isWSConnected
          }
          onClick={() => handleHearingDisconnect(primaryHearing.id)}
        />
      );
    }

    if (primaryHearing.status === 'connected') {
      return <CallDetailsButtonHearingDisconnect />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    primaryHearing.id,
    primaryHearing.status,
    primaryHearing.phoneNumber,
    isHearingReconnect,
    handleRegularHearingCall,
    voiceSessionStatus,
    isAcceptingUser,
    isLoadingPrimaryHearing,
  ]);

  const hearingToDeafCalleeButton = useMemo(() => {
    if (deaf.status === 'disconnected') {
      return (
        <>
          {deaf.connectionAttempts > 0 ? (
            <CallDetailsButtonReconnect
              loading={isLoadingDeaf}
              onClick={() => handleDeafConnect(deaf.phoneNumber)}
            />
          ) : (
            <CallDetailsButtonCalleeConnect
              isLoading={isLoadingDeaf}
              onClickHandler={() => handleDeafConnect(deaf.phoneNumber)}
            />
          )}
          {!isLoadingDeaf && (
            <ContainerWrapper>
              <CallDetailsButtonNewCall />
            </ContainerWrapper>
          )}
        </>
      );
    }

    if (deaf.status === 'connecting') {
      return (
        <>
          <CallDetailsButtonCancel onClick={handleDeafCancel} />
          {isDeafUserFromSorenson && (
            <ContainerWrapper>
              <CallDetailsSkipToSignMailButton
                onClick={handleSkipToSignMail}
                loading={isLoadingDeaf}
              />
            </ContainerWrapper>
          )}
        </>
      );
    }

    if (deaf.status === 'connected') {
      return (
        <CallDetailsDeafConnectedButtons
          isLoadingDeaf={isLoadingDeaf}
          isThreeDotsMenuButtonPresent={isThreeDotsMenuButtonPresent}
        />
      );
    }

    if (deaf.status === 'unavailable') {
      return (
        <CallDetailsButtonDisconnect
          loading={isLoadingDeaf}
          onClick={handleEndSignMailSession}
        />
      );
    }
  }, [
    deaf.status,
    deaf.connectionAttempts,
    deaf.phoneNumber,
    isLoadingDeaf,
    handleDeafConnect,
    handleDeafCancel,
    isDeafUserFromSorenson,
    handleSkipToSignMail,
    handleEndSignMailSession,
    isThreeDotsMenuButtonPresent,
  ]);

  const calleeMetadata = useMemo(() => {
    if (isDeafToHearing) {
      return primaryHearing;
    }
    return deaf;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    deaf.status,
    deaf.phoneNumber,
    primaryHearing.id,
    primaryHearing.status,
    primaryHearing.phoneNumber,
    primaryHearing.isWavelloRegistered,
    isDeafToHearing,
  ]);

  const calleeButton = useMemo(() => {
    if (isDeafToHearing) {
      return deafToHearingCalleeButton;
    }

    return (
      <>
        {hearingToDeafCalleeButton}
        <CallDetailsParticipantVco2LineContainer />
      </>
    );
  }, [deafToHearingCalleeButton, hearingToDeafCalleeButton, isDeafToHearing]);

  const callee = useMemo<CallDetailsParticipantInfo>(() => {
    return {
      ...calleeMetadata,
      icon: <CallDetailsParticipantIcon isDeafParticipant={!isDeafToHearing} />,
      children: calleeButton,
      testId: isDeafToHearing ? 'hearing-details-item' : 'deaf-details-item',
      title: calleeTitle,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    calleeMetadata,
    deafToHearingCalleeButton,
    hearingToDeafCalleeButton,
    isDeafToHearing,
  ]);

  return { callee };
};
